export const timesheetsArticles = [
    {
        id: 'timesheets.overview',
        title: 'Timesheets Overview',
        category: 'Timesheets',
        order: 1,
        summary: 'Track labour hours against jobs, approve submitted time, and let workers log their own hours',
        content: `
The Timesheets page tracks labour hours against your jobs. It has three tabs:

- **Entries** — All timesheet entries for a week, with filters, running totals, and per-row actions
- **Approvals** — Submitted entries waiting for sign-off, with bulk approve/reject (a badge on the tab shows the pending count)
- **Employees** — The workers who appear on timesheets (staff and subcontractor crews)

There is also a separate **My Timesheet** mobile page where workers without a Costed login enter their own time using an employee code and PIN. Their entries flow into the same Approvals queue.
    `,
        expandableSections: [
            {
                title: 'The Entries Tab',
                content: `
The Entries tab shows one week at a time (Monday–Sunday):

- **Week navigator** — Step backwards/forwards a week, or jump to **This week**
- **Filters** — By worker, by job, and by status (Draft, Submitted, Approved, Rejected)
- **Summary cards** — Entry count, total hours, total cost (ex GST), and total charge (ex GST) for the visible entries
- **Table** — Date, worker (subbies flagged with a truck icon), job, cost centre, hours, cost rate, cost, and status

Per-row actions depend on status:

- **Submit** (paper-plane icon) — Send a draft or rejected entry for approval
- **Approve** (tick icon) — Approve a submitted entry directly from the list
- **Edit** (pencil) — Open the entry for editing (not available once approved)
- **Un-approve** (unlock icon) — Unlock an approved entry so it can be edited again
- **Delete** (bin) — Remove an entry (not available once approved)
- **Audit history** (clock icon) — View the entry's submit/approve/reject trail

Double-click a row to edit it (approved entries must be un-approved first).
        `,
            },
            {
                title: 'Approval Workflow',
                content: `
Timesheet entries move through four statuses:

1. **Draft** — Editable by the worker or office
2. **Submitted** — Sent for approval; the worker can still correct it until it is approved
3. **Approved** — Signed off and locked for editing
4. **Rejected** — Sent back to the worker as an editable draft, with an optional reason

**Who sees and changes what:** below owner/admin/manager, a person sees, records, submits, edits and deletes only the entries of the employee record linked to their login (Settings → Employees → link a user); a login with no linked employee sees none. Owners, admins and managers see everyone's, record hours for anyone, and are the only ones who approve, reject, un-approve or change the self-entry lockout window. Nobody can approve their own hours.
The **Approvals** tab lists all submitted entries. Tick the entries you want to action (or **Select all**), then:

- **Approve Selected** — Approves and locks the ticked entries. A running total of selected hours and cost is shown next to the buttons.
- **Reject…** — Opens a dialog where you can add an optional reason (e.g. "Wrong cost centre — please re-allocate"). Rejected entries return to the worker as drafts so they can fix and re-submit.

On a phone, the Approvals tab switches to a card layout — tap cards to select them, then use the same Approve/Reject buttons. Approved entries can later be unlocked from the Entries tab with **Un-approve**.
        `,
            },
            {
                title: 'My Timesheet (Worker Self-Entry)',
                content: `
Workers who don't have a Costed user account can log their own time from a phone:

1. They open the **My Timesheet** link for your workspace (\`/timesheet/me/your-workspace\`)
2. They sign in with their **employee code** and a **4–6 digit PIN** (set in the Employees tab)
3. They see a Monday–Sunday week view with a card per day, day totals, and week totals
4. Tapping **+ Add** on a day opens an entry form: **Job** (searchable), **Cost centre** (optional), **Hours** (quarter-hour steps), **Rate** (Base, OT 1.5×, or OT 2×), and **Notes**
5. **Copy prev day** replicates yesterday's entries onto an empty day with one tap
6. **Submit week** sends all draft entries in the visible week for approval

Once submitted, an entry is locked for the worker — only a supervisor can change it. Workers can only add or edit time within the self-entry lockout window (see Timesheet Settings).

A My Timesheet sign-in is its own kind of session: it works only on the My Timesheet page and never on the main Costed app or API. After the September 2026 security update every worker signs in once more with their code and PIN.
        `,
            },
            {
                title: 'Timesheet Settings',
                content: `
Under **Settings → Timesheets** you can configure the self-entry behaviour:

- **Self-entry lockout window (days)** — How far in the past workers can enter their own time (1–365 days, default 14). Older entries can only be added by a supervisor in the Entries tab.
        `,
            },
        ],
        keywords: ['timesheets', 'hours', 'labour', 'time tracking', 'approvals', 'submit', 'approve', 'reject', 'my timesheet', 'week'],
        route: '/timesheets',
        relatedArticles: ['timesheets.entry', 'timesheets.employees', 'daily-logs.overview', 'jobs.overview'],
        screenshots: [
            {
                src: 'timesheets/timesheets-overview.png',
                alt: 'Timesheets page with Entries, Approvals, and Employees tabs',
                caption: 'The Entries tab with week navigator, filters, and summary totals',
            },
        ],
    },
    {
        id: 'timesheets.entry',
        title: 'Adding & Editing Timesheet Entries',
        category: 'Timesheets',
        order: 2,
        summary: 'Record hours against a job with cost and charge rates, optional allowance allocation, and a full audit history',
        content: `
Click **Add Entry** on the Entries tab to record time for any worker. The entry captures who worked, on which job and cost centre, for how long, and at what rates — so labour cost flows into job costing at the moment it's entered.

A live preview at the bottom of the form shows the line's cost and charge as you type (hours × rate). When editing an existing entry, the worker and job are fixed — only the other fields can change.
    `,
        expandableSections: [
            {
                title: 'Entry Fields',
                content: `
- **Worker** — Pick from your Employees list (subcontractor crews are marked "(subby)"). Locked when editing an existing entry.
- **Job** — The job the time was worked on. Locked when editing.
- **Cost centre** — Optional; searchable list of the selected job's cost centres.
- **Date** — The work date (defaults to today).
- **Hours** — 0.25 to 24, in quarter-hour steps (defaults to 8).
- **Cost rate** — $/hr ex GST; what the labour costs you.
- **Charge rate** — $/hr ex GST; what you'd charge it out at.
- **Allocate to allowance** — Optional; see below.
- **Notes** — Free text, up to 1000 characters.

Worker, job, date, and hours are required.
        `,
            },
            {
                title: 'Cost & Charge Rates',
                content: `
When you pick a worker on a new entry, the **cost rate** and **charge rate** default from that worker's employee record — you can override them per entry.

Rates are **snapshotted onto each entry**: changing an employee's default rate later doesn't rewrite historical entries, so past job costs stay accurate. The preview bar shows the resulting cost (hours × cost rate) and charge (hours × charge rate) as you edit.
        `,
            },
            {
                title: 'Allocating to an Allowance',
                content: `
If the selected job has allowances (e.g. PS/PC items), you can optionally allocate the entry to one via **Allocate to allowance**. The entry's labour cost then rolls up into that allowance's actual, so supervision or installation labour counts against the allowance rather than sitting as unallocated cost.

Allocated entries show a **→ allow** badge in the Entries and Approvals tables.
        `,
            },
            {
                title: 'Audit History',
                content: `
Every entry has an audit trail. Click the clock icon on a row (in the Entries or Approvals tab) to open the **Audit history** modal, which shows a timeline of:

- **Submitted** — when the entry was sent for approval, and by whom
- **Approved** — who approved it and when
- **Rejected** — who rejected it, when, and the reason given
- **Unlocked** — when an approved entry was un-approved for editing

Entries created before detailed history was introduced show only their basic submitted/approved information.
        `,
            },
        ],
        keywords: ['timesheet entry', 'add entry', 'hours', 'cost rate', 'charge rate', 'allowance', 'cost centre', 'notes', 'audit history'],
        route: '/timesheets',
        relatedArticles: ['timesheets.overview', 'timesheets.employees', 'jobs.overview', 'settings.cost-centres'],
        screenshots: [
            {
                src: 'timesheets/timesheet-entry.png',
                alt: 'Add Timesheet Entry modal',
                caption: 'Recording hours with worker, job, cost centre, rates, and the live cost preview',
            },
        ],
    },
    {
        id: 'timesheets.employees',
        title: 'Employees & Subcontractor Crews',
        category: 'Timesheets',
        order: 3,
        summary: 'Manage the workers who appear on timesheets — contact details, default rates, kiosk PINs, and archiving',
        content: `
The **Employees** tab holds the workers you record time for. Each worker is either an **Employee** (your own staff, badged EMP) or a **Subcontractor crew** (badged SUB).

The toolbar lets you filter by type (All / Staff / Subbies, with live counts), search by name, code, email, or linked supplier, and show archived workers. The table shows each worker's contact details, linked supplier, default cost and charge rates, and whether a kiosk PIN is set (key icon). Double-click a row — or use the pencil icon — to edit.
    `,
        expandableSections: [
            {
                title: 'Adding a Worker',
                content: `
Click **Add Employee** and fill in:

- **Worker type** — Employee or Subcontractor crew
- **First name / Last name**
- **Code** — Short code for quick entry (e.g. JS). This is also the sign-in code workers use on the My Timesheet mobile page.
- **Email / Phone** — Optional contact details
- **Linked supplier** — Subcontractor crews only; optional (see below)
- **Default cost rate / Default charge rate** — $/hour, ex GST
- **Kiosk PIN** — Optional, 4–6 digits, for mobile self-entry

At least one of first name, last name, or code is required.
        `,
            },
            {
                title: 'Employees vs Subcontractor Crews',
                content: `
Subcontractor crews still bill you through purchase orders — their employee record exists so their man-days can be logged on timesheets and rolled up against PS/PC allowances.

A subby crew can optionally be **linked to a supplier** so their ABN and insurance details live in one place on the supplier record. Subbies show a SUB badge and truck icon wherever they appear in timesheet lists.
        `,
            },
            {
                title: 'Default Rates',
                content: `
The **default cost rate** and **default charge rate** pre-fill new timesheet entries for that worker. Rates are snapshotted onto each entry when it's created — historical entries keep their original rate, so updating a worker's rate only affects entries from then on.
        `,
            },
            {
                title: 'Kiosk PINs & Archiving',
                content: `
- **Kiosk PIN** — Set a 4–6 digit PIN so a worker without a Costed login can sign in to the My Timesheet mobile page with their employee code + PIN. When editing, tick **Clear existing PIN** to revoke access. The Employees table shows a key icon for workers with a PIN set.
- **Archive** — Archiving a worker removes them from active lists but **keeps all their existing timesheet entries**. Tick **Show archived** to see archived workers, and use the restore button to bring one back.
        `,
            },
        ],
        keywords: ['employees', 'workers', 'subcontractor', 'subbies', 'cost rate', 'charge rate', 'kiosk pin', 'archive', 'supplier link'],
        route: '/timesheets',
        relatedArticles: ['timesheets.overview', 'timesheets.entry', 'suppliers.overview', 'settings.users'],
        screenshots: [
            {
                src: 'timesheets/employee-modal.png',
                alt: 'Add Employee modal',
                caption: 'Creating a worker with type, names, code, default rates, and kiosk PIN',
            },
        ],
    },
];
